import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Plus, MessageSquare, Play, DollarSign, Calendar,
  X, Sparkles, CheckCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface SmartAction {
  id: string;
  icon: React.ElementType;
  label: string;
  description?: string;
  priority: 'high' | 'medium' | 'low';
  onClick: () => void;
}

interface SmartFABProps {
  actions?: SmartAction[];
  nextSession?: {
    studentName: string;
    time: string;
    scheduleId?: string;
    studentId?: string;
  } | null;
  pendingPayments?: number;
  onConfirmClass?: () => void;
  onStartSession?: () => void;
  onChargePayments?: () => void;
  /** @deprecated Movido para FAB contextual da aba Alunos */
  onAddStudent?: () => void;
  /** @deprecated Movido para FAB contextual da aba Agenda */
  onScheduleClass?: () => void;
}

export function SmartFAB({
  actions = [],
  nextSession,
  pendingPayments = 0,
  onConfirmClass,
  onStartSession,
  onChargePayments,
}: SmartFABProps) {
  const [isOpen, setIsOpen] = useState(false);

  const smartActions = useMemo<SmartAction[]>(() => {
    // Build smart actions based on context
    const newActions: SmartAction[] = [];
    const hora = new Date().getHours();

    // Morning: Suggest confirming today's classes
    if (hora >= 6 && hora < 12 && nextSession && onConfirmClass) {
      newActions.push({
        id: 'confirm-classes',
        icon: CheckCircle,
        label: 'Confirmar aula de hoje',
        description: `${nextSession.studentName} às ${nextSession.time}`,
        priority: 'high',
        onClick: () => {
          onConfirmClass();
          setIsOpen(false);
        }
      });
    }

    // Next session today
    if (nextSession && onStartSession) {
      newActions.push({
        id: 'start-session',
        icon: Play,
        label: `Treino com ${nextSession.studentName}`,
        description: `Agendado para ${nextSession.time}`,
        priority: 'high',
        onClick: () => {
          onStartSession();
          setIsOpen(false);
        }
      });
    }

    // End of month: Suggest charging pending payments
    const hoje = new Date();
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).getDate();
    if (hoje.getDate() >= ultimoDia - 5 && pendingPayments > 0 && onChargePayments) {
      newActions.push({
        id: 'charge-pending',
        icon: DollarSign,
        label: `Cobrar ${pendingPayments} pendência${pendingPayments > 1 ? 's' : ''}`,
        description: 'Enviar lembretes de pagamento',
        priority: 'high',
        onClick: () => {
          onChargePayments();
          setIsOpen(false);
        }
      });
    }

    // Add custom actions
    newActions.push(...actions);

    // Sort by priority
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    newActions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    return newActions.slice(0, 4); // Max 4 actions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    actions,
    nextSession?.studentName,
    nextSession?.time,
    pendingPayments,
    onConfirmClass,
    onStartSession,
    onChargePayments,
  ]);

  const hasHighPriority = smartActions.some(a => a.priority === 'high');

  // Se não há nenhuma ação inteligente disponível, não renderiza o FAB global
  if (smartActions.length === 0) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* FAB Container */}
      <div data-smart-fab className="fixed bottom-20 right-4 z-50 flex flex-col-reverse items-end gap-3">
        {/* Action buttons */}
        <AnimatePresence>
          {isOpen && smartActions.map((action, index) => (
            <motion.div
              key={action.id}
              initial={{ opacity: 0, y: 20, scale: 0.8 }}
              animate={{ 
                opacity: 1, 
                y: 0, 
                scale: 1,
                transition: { delay: index * 0.05 }
              }}
              exit={{ 
                opacity: 0, 
                y: 20, 
                scale: 0.8,
                transition: { delay: (smartActions.length - index) * 0.03 }
              }}
              className="flex items-center gap-3"
            >
              <div className={cn(
                "px-3 py-2 rounded-lg shadow-lg text-sm",
                action.priority === 'high' 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-card border"
              )}>
                <p className="font-medium">{action.label}</p>
                {action.description && (
                  <p className={cn(
                    "text-xs",
                    action.priority === 'high' 
                      ? "text-primary-foreground/80" 
                      : "text-muted-foreground"
                  )}>
                    {action.description}
                  </p>
                )}
              </div>
              
              <Button
                size="icon"
                onClick={action.onClick}
                className={cn(
                  "h-12 w-12 rounded-full shadow-lg",
                  action.priority === 'high' && "bg-primary hover:bg-primary/90"
                )}
                variant={action.priority === 'high' ? 'default' : 'secondary'}
              >
                <action.icon className="h-5 w-5" />
              </Button>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Main FAB */}
        <motion.div
          initial={false}
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Button
            size="icon"
            onClick={() => setIsOpen(!isOpen)}
            className={cn(
              "h-14 w-14 rounded-full shadow-xl",
              hasHighPriority && !isOpen && "animate-pulse bg-primary"
            )}
          >
            {isOpen ? (
              <X className="h-6 w-6" />
            ) : hasHighPriority ? (
              <Sparkles className="h-6 w-6" />
            ) : (
              <Plus className="h-6 w-6" />
            )}
          </Button>
        </motion.div>

        {/* Notification dot for high priority */}
        {hasHighPriority && !isOpen && (
          <motion.div
            className="absolute top-0 right-0 w-4 h-4 bg-destructive rounded-full"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring" }}
          />
        )}
      </div>
    </>
  );
}
